import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, GridReadyEvent, PaginationChangedEvent } from 'ag-grid-community';
import classNames from 'classnames/bind';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import { formatNumber } from 'modules/I18N';
import { isValidIndex } from 'utils/numberUtils';
import { Store } from 'components/common/types/Store.types';
import { TableProps, TableTheme, TableHeight, TableRowHeight, Pagination } from './Table.types';
import LoadingOverlayComponent from './LoadingOverlayComponent';

import styles from './Table.pcss';

const cx = classNames.bind(styles);

const Table = <TData,>({
  className,
  columnDefs,
  dataTestId,
  extraGridOptions,
  getColumnApi,
  getTableApi,
  id,
  localeText = { noRowsToShow: 'No Rows To Show' },
  onCellClicked,
  onGridSizeChanged,
  onRowClicked,
  onRowDataUpdated,
  ref,
  rowData,
  rowHeight = TableRowHeight.NORMAL,
  suppressMovableColumns = true,
  syncPageIndex,
  tableHeight = TableHeight.NORMAL,
  theme = TableTheme.ALPINE,
}: TableProps<TData>): React.ReactElement => {
  const { localeCode } = useSelector((state: Store) => state.publisher.configuration);
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 0,
    totalPages: 0,
    totalResults: 0,
    startResult: 0,
    endResult: 0,
  });
  const [gridApi, setGridApi] = useState<GridApi<TData> | undefined>();

  const autoFitColumn = (params: GridReadyEvent<TData>): void => {
    params.api.sizeColumnsToFit();
  };
  const onGridReady = (params: GridReadyEvent<TData>): void => {
    setGridApi(params.api);
    getTableApi?.(params.api);
    getColumnApi?.(params.columnApi);
    autoFitColumn(params);
  };

  const onPaginationChanged = (params: PaginationChangedEvent): void => {
    if (!params.api) return;

    const apiCurrentPage = params.api.paginationGetCurrentPage();
    const totalPages = params.api.paginationGetTotalPages();
    const totalResults = params.api.paginationGetRowCount();
    const pageSize = params.api.paginationGetPageSize();

    setPagination((prev) => {
      const currentPage = isValidIndex(syncPageIndex) ? prev?.currentPage : apiCurrentPage;
      const startResult = currentPage * pageSize + 1;
      const endResult = Math.min(startResult + pageSize - 1, totalResults);

      return { currentPage, totalPages, totalResults, startResult, endResult };
    });
  };

  useEffect(() => {
    if (!isValidIndex(syncPageIndex)) return;

    setPagination((prev) => ({
      ...prev,
      currentPage: syncPageIndex as number,
    }));
  }, [syncPageIndex]);

  const baseGridOptions = {
    onGridReady,
    onGridSizeChanged: autoFitColumn,
    headerHeight: 50,
    suppressMovableColumns,
    defaultColDef: {
      resizable: true,
      wrapHeaderText: true,
    },
    suppressPaginationPanel: true,
    onPaginationChanged,
  };

  const gridOptions = {
    ...baseGridOptions,
    getRowHeight: () => rowHeight,
    ...(extraGridOptions ?? {}),
  };

  const goToFirstPage = (): void => {
    gridApi?.paginationGoToFirstPage();
  };

  const goToLastPage = (): void => {
    gridApi?.paginationGoToLastPage();
  };

  const goToNextPage = (): void => {
    gridApi?.paginationGoToNextPage();
  };

  const goToPreviousPage = (): void => {
    gridApi?.paginationGoToPreviousPage();
  };

  return (
    <div id={id} data-test-id={dataTestId} className={className ?? cx('table', theme, tableHeight)}>
      <AgGridReact<TData>
        data-testid={dataTestId}
        ref={ref}
        gridOptions={gridOptions}
        columnDefs={columnDefs}
        rowData={rowData}
        onRowClicked={onRowClicked}
        onCellClicked={onCellClicked}
        loadingOverlayComponent={LoadingOverlayComponent}
        localeText={localeText}
        onRowDataUpdated={onRowDataUpdated}
        onGridSizeChanged={onGridSizeChanged}
      />
      {gridOptions.pagination && pagination.totalPages > 1 && (
        <div className="ag-paging-panel ag-unselectable">
          <span className="body-sm text-center">
            <span>{formatNumber({ value: pagination.startResult, localeCode })}</span>
            <span> to </span>
            <span>{formatNumber({ value: pagination.endResult, localeCode })}</span>
            <span> of </span>
            <span>{formatNumber({ value: pagination.totalResults, localeCode })}</span>
          </span>
          <span className="ag-paging-page-summary-panel" role="presentation">
            <div
              data-test-id="first-page"
              className={cx('ag-paging-button', { 'ag-disabled': pagination?.currentPage === 0 })}
              role="presentation"
              onClick={goToFirstPage}
            >
              <span className="ag-icon ag-icon-first" unselectable="on" />
            </div>
            <div
              data-test-id="previous-page"
              className={cx('ag-paging-button', { 'ag-disabled': pagination?.currentPage === 0 })}
              role="presentation"
              onClick={goToPreviousPage}
            >
              <span className="ag-icon ag-icon-previous" unselectable="on" />
            </div>
            <span className="body-sm text-center">
              <span> Page </span>
              <span>{formatNumber({ value: pagination.currentPage + 1, localeCode })}</span>
              <span> of </span>
              <span>{formatNumber({ value: pagination.totalPages, localeCode })}</span>
            </span>
            <div
              data-test-id="next-page"
              className={cx('ag-paging-button', {
                'ag-disabled': pagination?.currentPage === (pagination?.totalPages ?? 1) - 1,
              })}
              role="presentation"
              onClick={goToNextPage}
            >
              <span className="ag-icon ag-icon-next" unselectable="on" />
            </div>
            <div
              data-test-id="last-page"
              className={cx('ag-paging-button', {
                'ag-disabled': pagination?.currentPage === (pagination?.totalPages ?? 1) - 1,
              })}
              role="presentation"
              onClick={goToLastPage}
            >
              <span className="ag-icon ag-icon-last" unselectable="on" />
            </div>
          </span>
        </div>
      )}
    </div>
  );
};

export default Table;
